import { Link } from 'react-router-dom';

import { Logo } from '@components/Logo';
import { Container } from '@components/common/Container';
import { PRIVACY_POLICY_PATH, TERMS_OF_SERVICE_PATH } from '@constants/legal';
import { PUBLIC_FOOTER_RESOURCE_LINKS, PUBLIC_NAV_LINKS, VISIT_PATH } from '@constants/publicSearch';
import { withPublicSiteView } from '@constants/publicSiteView';
import { legalLinkStateFromLandingFooter } from '@utils/legalBackNavigation';
import { usePublicSiteSettings } from '@contexts/PublicSiteSettingsContext';

export interface LandingFooterProps {
  isDarkMode: boolean;
  /** Signed-in member previewing the public site (`?view=public`). */
  viewingPublicSite?: boolean;
}

const ADDRESS = {
  name: 'Bethlehem Baptist Church',
  street: '8400 Poole Road',
  city: 'Knightdale, NC 27545',
};

/** Areas served — Knightdale first, then alphabetical */
const AREAS_SERVED =
  'Knightdale, Clayton, Raleigh, Wake Forest, Wendell, Zebulon';

export function LandingFooter({ isDarkMode, viewingPublicSite = false }: LandingFooterProps) {
  const { directorEmail } = usePublicSiteSettings();

  return (
    <footer id="site-footer" className="bhg-landing-footer" role="contentinfo">
      <Container size="lg">
        <div className="bhg-landing-footer__content">
          <div className="bhg-landing-footer__brand">
            <Logo
              variant={isDarkMode ? 'lightBlue' : 'white'}
              size="medium"
              showText={false}
            />
            <p className="bhg-landing-footer__description">
              A Christian-led homeschool group providing education and support
              for homeschooling families in {AREAS_SERVED}, and the greater Triangle, NC.
            </p>
          </div>

          <div className="bhg-landing-footer__column">
            <h4 className="bhg-landing-footer__heading">Quick Links</h4>
            <nav className="bhg-landing-footer__nav" aria-label="Footer">
              {PUBLIC_NAV_LINKS.map(({ href, label }) => (
                <Link
                  key={href}
                  to={withPublicSiteView(href, viewingPublicSite)}
                  className="bhg-landing-footer__link"
                >
                  {label}
                </Link>
              ))}
              <Link
                to={withPublicSiteView(VISIT_PATH, viewingPublicSite)}
                state={{ visitSource: 'footer' }}
                className="bhg-landing-footer__link"
              >
                Schedule a Visit
              </Link>
            </nav>
          </div>

          <div className="bhg-landing-footer__column">
            <h4 className="bhg-landing-footer__heading">Resources</h4>
            <nav className="bhg-landing-footer__nav" aria-label="Resources">
              {PUBLIC_FOOTER_RESOURCE_LINKS.map(({ href, label }) => (
                <Link
                  key={label}
                  to={withPublicSiteView(href, viewingPublicSite)}
                  className="bhg-landing-footer__link"
                >
                  {label}
                </Link>
              ))}
            </nav>
          </div>

          <div className="bhg-landing-footer__column">
            <h4 className="bhg-landing-footer__heading">Contact</h4>
            <p className="bhg-landing-footer__address">
              {ADDRESS.name}
              <br />
              {ADDRESS.street}
              <br />
              {ADDRESS.city}
            </p>
            <a href={`mailto:${directorEmail}`} className="bhg-landing-footer__link">
              Email Director
            </a>
          </div>
        </div>

        <div className="bhg-landing-footer__bottom">
          <p className="bhg-landing-footer__areas">
            Serving homeschool families in {AREAS_SERVED}, and the greater Triangle, NC.
          </p>
          <nav className="bhg-landing-footer__legal" aria-label="Legal">
            <Link
              to={TERMS_OF_SERVICE_PATH}
              state={legalLinkStateFromLandingFooter()}
              className="bhg-landing-footer__link"
            >
              Terms of Service
            </Link>
            <span className="bhg-landing-footer__legal-sep" aria-hidden>
              ·
            </span>
            <Link
              to={PRIVACY_POLICY_PATH}
              state={legalLinkStateFromLandingFooter()}
              className="bhg-landing-footer__link"
            >
              Privacy Policy
            </Link>
          </nav>
          <p>© {new Date().getFullYear()} Bethlehem Homeschool Group. All rights reserved.</p>
        </div>
      </Container>
    </footer>
  );
}
